import { ReactNode } from 'react';

import styled from 'styled-components';

import { FileDropzone } from '@components/file-dropzone';
import { UploadedFile } from '@components/file-field/uploaded-file';
import { ContentFileFieldType, MappedDefaultValue } from '@components/form/v2';
import { IconButton } from '@components/icon-button';
import { IconDownload } from '@components/icons/download';
import { IconFile } from '@components/icons/file';
import { IconOpenPreview } from '@components/icons/open-preview';
import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { getScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { FieldDropzone } from './field-dropzone';
import { FieldMappingDisplay } from './use-field-mapping';

import { conditionsTab } from '../conditions-tab';
import { useFormBuilderContext } from '../context';
import { DefaultValueMapping } from '../default-value-mapping';
import { FieldEdit } from '../field-edit';
import { FieldConfig } from '../field-registry';
import { FieldSidebarLabel } from '../fields-sidebar';

type Config = FieldConfig<'content-file'>;

export type ContentFileState = 'static' | 'mapped' | 'empty';

/**
 * A content-file is either a builder-uploaded static file (`url`/`fileId`), a
 * file mapped from a journey variable (`mappedDefaultValue`), or empty. Static
 * and mapped are mutually exclusive; static wins if both are ever set.
 */
export const getContentFileState = (
  field: Pick<ContentFileFieldType, 'url' | 'mappedDefaultValue'>
): ContentFileState => {
  if (field.url) {
    return 'static';
  }
  if (field.mappedDefaultValue?.value) {
    return 'mapped';
  }
  return 'empty';
};

const getDisplayName = (field: { fileAlias: string; fileName: string }) => {
  return field.fileAlias ?? field.fileName;
};

/**
 * A file coming from a journey variable, shown as the variable pill in place of
 * a filename. Also used for mapped input-file rows in the preview, which pass a
 * single occurrence as `mappedDefaultValue` and a replace/remove `extraAction`.
 */
export const MappedFileCard = ({
  label,
  mappedDefaultValue,
  isPreview = false,
  extraAction,
  dataTestId = 'mapped-content-file-card',
}: {
  label?: string;
  mappedDefaultValue?: MappedDefaultValue;
  isPreview?: boolean;
  extraAction?: ReactNode;
  dataTestId?: string;
}) => {
  if (!mappedDefaultValue?.value) {
    return <ContentFileFallback />;
  }
  const hasActions = isPreview || Boolean(extraAction);
  return (
    <Stack space={1}>
      {label && <Text font="bold14" text={label} />}
      <UploadedFile
        dataTestId={dataTestId}
        isDisabled={!hasActions}
        nameContent={
          <FieldMappingDisplay content={mappedDefaultValue} textProps={{ font: 'regular16' }} />
        }
        actions={
          <>
            {isPreview && (
              <>
                <IconButton
                  variant="ghost-purple"
                  icon={<IconOpenPreview />}
                  disabled
                  tooltip={t('noFileToPreview')}
                />
                <IconButton
                  variant="ghost-purple"
                  icon={<IconDownload />}
                  disabled
                  tooltip={t('noFileToDownload')}
                />
              </>
            )}
            {extraAction}
          </>
        }
      />
    </Stack>
  );
};

const Display: Config['Display'] = ({ field }) => {
  const state = getContentFileState(field);

  if (state === 'mapped') {
    return <MappedFileCard label={field.label} mappedDefaultValue={field.mappedDefaultValue} />;
  }

  if (state === 'empty') {
    return <ContentFileFallback />;
  }

  return (
    <Stack space={1}>
      {field.label && <Text font="bold14" text={field.label} />}
      <UploadedFile name={getDisplayName(field)} isDisabled />
    </Stack>
  );
};

const FormBuilderContentFile: Config['Component'] = ({ field, dragHandle }) => {
  const showPreview = usePreviewFileModal();
  const {
    store: { onFileSave, actions: formActions },
  } = useFormBuilderContext();

  const setFileFields = (url: string, fileName: string, fileId: string, urlConverted?: string) => {
    formActions.createChangeHandler(field, 'fileName')(fileName);
    formActions.createChangeHandler(field, 'fileId')(fileId);
    formActions.createChangeHandler(field, 'fileAlias')(fileName);
    formActions.createChangeHandler(field, 'url')(url);
    if (urlConverted) {
      formActions.createChangeHandler(field, 'urlConverted')(urlConverted);
    }
  };

  const clearMapping = () => {
    formActions.createChangeHandler(field, 'mappedDefaultValue')({ value: '', variables: {} });
  };

  const handleUpload = async (file: File, setProgress?: (progress: number) => void) => {
    const data = new FormData();
    data.append(field.id, file);
    let result;
    try {
      formActions.startSaving();
      result = await onFileSave?.({ data, setProgress });
    } finally {
      formActions.stopSaving();
    }
    if (!result) return;
    // A builder-supplied static file and a mapped file are mutually exclusive.
    clearMapping();
    setFileFields(result.url, result.fileName, result.fileId, result.urlConverted);
  };

  const displayName = getDisplayName(field);
  const state = getContentFileState(field);

  return (
    <FieldDropzone field={field}>
      <FieldEdit
        field={field}
        dragHandle={dragHandle}
        display={<Display field={field} />}
        edit={
          <Stack space={4}>
            <TextField
              onChange={formActions.createChangeHandler(field, 'label')}
              defaultValue={field.label}
              label={t('label')}
              placeholder={t('FilePlaceholder')}
            />
            {state === 'static' ? (
              <>
                <TextField
                  key={field.fileId}
                  onChange={(value) =>
                    formActions.createChangeHandler(field, 'fileAlias')(value || field.fileName)
                  }
                  defaultValue={displayName}
                  label={t('fileAlias')}
                />
                <UploadedFile
                  name={displayName}
                  path={field.url}
                  onPreview={() =>
                    showPreview({
                      formFile: {
                        name: displayName,
                        src: field.url,
                        srcConverted: field.urlConverted,
                        originalFileName: field.fileName,
                      },
                    })
                  }
                  onRemove={() => setFileFields('', '', '')}
                />
              </>
            ) : (
              <>
                {state === 'empty' && <FileDropzone handleUpload={handleUpload} />}
                <DefaultValueMapping field={field} />
              </>
            )}
          </Stack>
        }
      />
    </FieldDropzone>
  );
};

export const ContentFileFallback = () => (
  <FallbackContainer>
    <IconFile size={40} color="purple50" />
    <Text font="regular14" color="purple50" text={t('noFile')} />
  </FallbackContainer>
);

const { t } = getScopedTranslate({
  en: {
    title: 'File',
    noFile: 'No file added',
    label: 'Label',
    fileAlias: 'File display name',
    FilePlaceholder: 'File label...',
    noFileToPreview: 'No file to preview',
    noFileToDownload: 'No file to download',
  },
});

export const contentFileConfig: Config = {
  kind: 'content-file',
  title: t('title'),
  group: 'content',
  Icon: IconFile,
  Component: FormBuilderContentFile,
  Display,
  TreeDisplay: ({ field }) => <FieldSidebarLabel label={field.label || t('title')} />,
  propertiesTabs: [conditionsTab],
};

const FallbackContainer = styled.div({
  borderRadius: pxToRem(8),
  background: theme.color.uiTrans5,
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: pxToRem(16),
});
